import { useState } from 'react';
import { ArrowRight, ArrowUp, X, Sparkles, Linkedin, Twitter, Instagram, Youtube, Server } from 'lucide-react';
import { Container, Section, H2, Lead, FadeUp, Logo } from './shared';

export const FinalCTA = ({ onStart }) => {
  const [email, setEmail] = useState('');
  const submit = (e) => { e.preventDefault(); onStart(email); };
  return (
    <Section testId="final-cta-section">
      <Container>
        <FadeUp>
          <div className="ld-cta-panel p-8 sm:p-12 lg:p-16 text-center">
            <div className="relative">
              <H2 className="!text-3xl lg:!text-5xl">Ready to run operations on autopilot?</H2>
              <Lead className="mt-4 max-w-xl mx-auto">Join 500+ teams who replaced spreadsheets and WhatsApp chaos with one AI-driven workspace.</Lead>
              <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                  className="flex-1 px-5 py-3.5 rounded-xl bg-black/40 border border-white/15 text-slate-100 placeholder:text-slate-500 outline-none focus:border-[#E06D53]/60 text-sm"
                  data-testid="cta-email-input"
                />
                <button type="submit" className="ld-btn-primary text-base !px-7 !py-3.5" data-testid="cta-start-btn">Start free <ArrowRight className="w-4 h-4" /></button>
              </form>
              <p className="text-xs text-slate-500 mt-4">Free plan forever · No credit card · Setup in 5 minutes</p>
            </div>
          </div>
        </FadeUp>
      </Container>
    </Section>
  );
};

const COLS = {
  Modules: ['Work Reporter', 'Complaints', 'Sales Tracker', 'Attendance', 'Tasks', 'Notice Board'],
  Company: ['About', 'Careers', 'Blog', 'Press kit', 'Contact'],
  Resources: ['Help center', 'Documentation', 'API reference', 'Status', 'Community'],
  Legal: ['Privacy policy', 'Terms of service', 'Cookie policy', 'Security', 'GDPR'],
};

export const LandingFooter = () => (
  <footer className="border-t border-white/[0.06] bg-[#050607]" data-testid="footer">
    <Container className="py-16">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2">
          <Logo />
          <p className="text-sm text-slate-500 mt-3 max-w-xs">Where business gets done. AI-powered operations for Indian enterprises.</p>
          <span className="ld-badge mt-5"><Server className="w-3 h-3 text-emerald-400" /> India data centre</span>
          <div className="flex items-center gap-2 mt-6">
            {[Linkedin, Twitter, Instagram, Youtube].map((Icon, i) => (
              <a key={i} href="#" className="p-2 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-white/5 transition-colors" data-testid={`footer-social-${i}`}><Icon className="w-4 h-4" /></a>
            ))}
          </div>
        </div>
        {Object.entries(COLS).map(([title, links]) => (
          <div key={title}>
            <p className="ld-mono text-[11px] tracking-[0.2em] uppercase text-slate-400 mb-4">{title}</p>
            {links.map((l) => <a key={l} href={l === 'Blog' ? '/blog' : l === 'Status' ? '/dashboard/status' : '#'} className="block text-sm text-slate-500 hover:text-slate-200 py-1.5 transition-colors">{l}</a>)}
          </div>
        ))}
      </div>
      <div className="mt-14 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-600">
        <p>© 2026 Woxora. All rights reserved. Made in India.</p>
        <p className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#E06D53]" /> Powered by Claude AI by Anthropic</p>
      </div>
    </Container>
  </footer>
);

export const FloatingBits = ({ toast, toastExiting, showBackToTop, showCookie, onCookie, exitIntent, onCloseExit }) => (
  <>
    {toast && (
      <div className={`fixed bottom-24 left-6 z-50 ${toastExiting ? 'ld-toast-out' : 'ld-toast-in'}`} data-testid="social-proof-toast">
        <div className="ld-glass rounded-xl px-4 py-3 flex items-center gap-3 shadow-2xl">
          <span className="w-2 h-2 rounded-full bg-emerald-400 ld-pulse-dot" />
          <p className="text-sm text-slate-200"><strong className="text-slate-50">{toast.name}</strong> {toast.action}</p>
        </div>
      </div>
    )}
    {showBackToTop && (
      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-20 right-6 z-50 w-11 h-11 rounded-full ld-btn-primary !p-0" aria-label="Back to top" data-testid="back-to-top"><ArrowUp className="w-5 h-5" /></button>
    )}
    {exitIntent && (
      <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="exit-intent-popup">
        <div className="ld-card !bg-[#121316] p-8 max-w-md w-full relative">
          <button onClick={onCloseExit} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200" aria-label="Close" data-testid="exit-intent-close"><X className="w-5 h-5" /></button>
          <h3 className="ld-display text-2xl font-bold text-slate-100">Want a walkthrough of Woxora?</h3>
          <p className="text-sm text-slate-400 mt-2">Leave your email and our team will set up a 20-minute demo for your use case.</p>
          <form onSubmit={(e) => { e.preventDefault(); onCloseExit(); }} className="flex gap-2 mt-6">
            <input type="email" placeholder="you@company.com" className="flex-1 px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-slate-100 text-sm outline-none focus:border-[#E06D53]/60" data-testid="exit-intent-email" />
            <button type="submit" className="ld-btn-primary" data-testid="exit-intent-submit">Book demo</button>
          </form>
        </div>
      </div>
    )}
    {showCookie && (
      <div className="fixed bottom-0 inset-x-0 z-[80] border-t border-white/10 bg-[#08090A]/95 backdrop-blur-xl" data-testid="cookie-consent">
        <Container className="py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">We use cookies to improve your experience. By continuing, you agree to our cookie policy.</p>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => onCookie('essential')} className="ld-btn-ghost !py-2 !px-4 !text-xs" data-testid="cookie-manage">Essential only</button>
            <button onClick={() => onCookie('all')} className="ld-btn-primary !py-2 !px-4 !text-xs" data-testid="cookie-accept">Accept all</button>
          </div>
        </Container>
      </div>
    )}
  </>
);
